import { computed, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { AuthUser, Roles } from '@tran-monorepo/interfaces';
import { tap } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private msalService = inject(MsalService);
    private msalBroadcastService = inject(MsalBroadcastService);

    private _isAuthenticated: WritableSignal<boolean> = signal<boolean>(false);
    private _isAuthLoading: WritableSignal<boolean> = signal<boolean>(true);
    private _userAccount: WritableSignal<AccountInfo | null> = signal<AccountInfo | null>(null);

    public isAuthenticated: Signal<boolean> = computed(() => this._isAuthenticated());
    public isAuthLoading: Signal<boolean> = computed(() => this._isAuthLoading());
    public userIdTokenClaims: Signal<AuthUser | null> = computed(() => this._userAccount()?.idTokenClaims as AuthUser);
    public userName: Signal<string | null> = computed(
        () => `${this.userIdTokenClaims()?.preferred_family_name}, ${this.userIdTokenClaims()?.preferred_given_name}`
    );
    public isAdmin: Signal<boolean> = computed(() => this.userIdTokenClaims()?.roles?.includes(Roles.ADMIN) || false);

    constructor() {
        this.initializeAuth();
    }

    public logout(): void {
        this.msalService.logout();
    }

    private initializeAuth(): void {
        this.subscribeToLogin();
        this.subscribeToLogout();
    }

    private subscribeToLogin(): void {
        this.msalBroadcastService.inProgress$
            .pipe(
                tap((status: InteractionStatus) => {
                    this._isAuthLoading.set(status !== InteractionStatus.None);

                    if (status === InteractionStatus.None) {
                        this.processLoginDoneAndUpdateUserData();
                    }
                })
            )
            .subscribe();
    }

    private subscribeToLogout(): void {
        this.msalBroadcastService.msalSubject$
            .pipe(
                tap((msg: EventMessage) => {
                    if (msg.eventType === EventType.ACCOUNT_REMOVED || msg.eventType === EventType.LOGOUT_SUCCESS) {
                        this._isAuthenticated.set(false);
                        this._userAccount.set(null);
                    }
                })
            )
            .subscribe();
    }

    private processLoginDoneAndUpdateUserData(): void {
        const accounts = this.msalService.instance.getAllAccounts();

        if (accounts.length > 0) {
            const activeAccount = this.msalService.instance.getActiveAccount() || accounts[0];

            if (activeAccount) {
                this.msalService.instance.setActiveAccount(activeAccount);
                this._userAccount.set(activeAccount);
                this._isAuthenticated.set(true);
            } else {
                this.setUnauthorizedUserSignals();
            }
        } else {
            this.setUnauthorizedUserSignals();
        }

        this._isAuthLoading.set(false);
    }

    private setUnauthorizedUserSignals() {
        this._isAuthenticated.set(false);
        this._userAccount.set(null);
    }
}
